import React, { useEffect, useState } from 'react';
import { Button } from './Button';
import { ArrowLeftIcon, PlayIcon, PauseIcon, VolumeIcon, Volume2Icon, ThumbsUpIcon, ThumbsDownIcon } from 'lucide-react';
interface ExercisePlayerProps {
  exerciseId: string;
  onBack: () => void;
  onComplete: () => void;
}
// Dummy exercise data
const exerciseData = {
  'stress-1': {
    id: 'stress-1',
    title: 'Ademhalingsoefening',
    type: 'exercise',
    duration: '3 min',
    instructions: ['Ga in een comfortabele positie zitten of liggen', 'Adem in door je neus gedurende 4 tellen', 'Houd je adem vast voor 2 tellen', 'Adem uit door je mond gedurende 6 tellen', 'Herhaal dit patroon voor 3 minuten'],
    tips: 'Focus op je ademhaling en probeer andere gedachten los te laten. Als je merkt dat je afdwaalt, breng je aandacht vriendelijk terug naar je ademhaling.'
  },
  'stress-2': {
    id: 'stress-2',
    title: 'Bodyscan meditatie',
    type: 'meditation',
    duration: '8 min',
    audioInstructions: 'Deze meditatie leidt je door een bodyscan, waarbij je aandacht systematisch langs verschillende delen van je lichaam gaat. Zo leer je spanning herkennen en loslaten.',
    tips: 'Zoek een rustige plek waar je niet gestoord wordt. Je kunt deze oefening zittend of liggend doen.'
  }
  // ... other exercises would be defined here
};
const ExercisePlayer: React.FC<ExercisePlayerProps> = ({
  exerciseId,
  onBack,
  onComplete
}) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [volume, setVolume] = useState(80);
  const [showFeedback, setShowFeedback] = useState(false);
  const [timer, setTimer] = useState(0);
  const exercise = exerciseData[exerciseId as keyof typeof exerciseData] || {
    id: 'unknown',
    title: 'Oefening',
    type: 'exercise',
    duration: '5 min',
    instructions: ['Instructies niet beschikbaar'],
    tips: 'Geen tips beschikbaar'
  };
  const totalDuration = parseInt(exercise.duration.split(' ')[0]) * 60; // Convert minutes to seconds
  useEffect(() => {
    let interval: NodeJS.Timeout | null = null;
    if (isPlaying) {
      interval = setInterval(() => {
        setTimer(prev => {
          const newTime = prev + 1;
          const newProgress = newTime / totalDuration * 100;
          setProgress(newProgress);
          if (newTime >= totalDuration) {
            setIsPlaying(false);
            setShowFeedback(true);
            if (interval) clearInterval(interval);
          }
          return newTime;
        });
      }, 1000);
    } else if (interval) {
      clearInterval(interval);
    }
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [isPlaying, totalDuration]);
  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs < 10 ? '0' : ''}${secs}`;
  };
  const handlePlayPause = () => {
    setIsPlaying(!isPlaying);
  };
  const handleVolumeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setVolume(parseInt(e.target.value));
  };
  const handleFeedback = (positive: boolean) => {
    // In a real app, you would send this feedback to your backend
    onComplete();
  };
  return <div className="w-full max-w-2xl mx-auto py-8" data-id="element-369">
      <div className="flex items-center mb-8" data-id="element-370">
        <Button variant="secondary" onClick={onBack} leftIcon={<ArrowLeftIcon className="w-4 h-4" data-id="element-372" />} data-id="element-371">
          Terug
        </Button>
      </div>
      <div className="mb-8" data-id="element-373">
        <h1 className="text-2xl font-light text-gray-800 mb-2" data-id="element-374">
          {exercise.title}
        </h1>
        <p className="text-sm text-gray-600" data-id="element-375">{exercise.duration}</p>
      </div>
      <div className="p-8 border-2 border-gray-300 rounded-xl mb-10" data-id="element-376">
        <div className="flex justify-center items-center h-40 mb-8" data-id="element-377">
          <div className="w-32 h-32 rounded-full border-2 border-gray-400 flex items-center justify-center" data-id="element-378">
            {exercise.type === 'meditation' ? <Volume2Icon className="w-16 h-16 text-gray-700" data-id="element-379" /> : <PlayIcon className="w-16 h-16 text-gray-700" data-id="element-380" />}
          </div>
        </div>
        <div className="mb-6" data-id="element-381">
          <div className="w-full h-3 bg-gray-200 rounded-full overflow-hidden" data-id="element-382">
            <div className="h-full bg-gray-500 rounded-full transition-all duration-200" style={{
            width: `${progress}%`
          }} data-id="element-383"></div>
          </div>
          <div className="flex justify-between mt-2 text-sm text-gray-500" data-id="element-384">
            <span data-id="element-385">{formatTime(timer)}</span>
            <span data-id="element-386">{formatTime(totalDuration)}</span>
          </div>
        </div>
        <div className="flex justify-center mb-8" data-id="element-387">
          <Button size="large" onClick={handlePlayPause} leftIcon={isPlaying ? <PauseIcon className="w-5 h-5" data-id="element-389" /> : <PlayIcon className="w-5 h-5" data-id="element-390" />} data-id="element-388">
            {isPlaying ? 'Pauzeren' : 'Starten'}
          </Button>
        </div>
        {exercise.type === 'meditation' && <div className="flex items-center" data-id="element-391">
            <VolumeIcon className="w-5 h-5 text-gray-500 mr-3" data-id="element-392" />
            <input type="range" min="0" max="100" value={volume} onChange={handleVolumeChange} className="w-full h-2 bg-gray-200 rounded-full appearance-none" data-id="element-393" />
            <span className="ml-3 text-sm text-gray-500" data-id="element-394">{volume}%</span>
          </div>}
      </div>
      <div className="mb-10" data-id="element-395">
        <h2 className="text-lg font-medium text-gray-800 mb-4" data-id="element-396">
          {exercise.type === 'meditation' ? 'Over deze meditatie' : 'Instructies'}
        </h2>
        {exercise.type === 'meditation' ? <p className="text-gray-600 mb-6" data-id="element-397">{exercise.audioInstructions}</p> : <ol className="list-decimal pl-6 mb-6 space-y-3" data-id="element-398">
            {exercise.instructions.map((instruction, index) => <li key={index} className="text-gray-600" data-id="element-399">
                {instruction}
              </li>)}
          </ol>}
        <div className="p-5 bg-gray-50 border-2 border-gray-300 rounded-lg" data-id="element-400">
          <h3 className="text-base font-medium text-gray-800 mb-2" data-id="element-401">Tips</h3>
          <p className="text-sm text-gray-600" data-id="element-402">{exercise.tips}</p>
        </div>
      </div>
      {showFeedback && <div className="p-8 border-2 border-gray-300 rounded-xl" data-id="element-403">
          <h2 className="text-lg font-medium text-gray-800 mb-6 text-center" data-id="element-404">
            Hoe vond je deze oefening?
          </h2>
          <div className="flex justify-center space-x-6" data-id="element-405">
            <Button variant="secondary" size="large" leftIcon={<ThumbsUpIcon className="w-5 h-5" data-id="element-407" />} onClick={() => handleFeedback(true)} data-id="element-406">
              Nuttig
            </Button>
            <Button variant="secondary" size="large" leftIcon={<ThumbsDownIcon className="w-5 h-5" data-id="element-409" />} onClick={() => handleFeedback(false)} data-id="element-408">
              Niet nuttig
            </Button>
          </div>
        </div>}
    </div>;
};
export default ExercisePlayer;